import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { 
  CheckCircle2, 
  Users, 
  Target,
  Heart,
  Phone,
  TrendingUp,
  Clock,
  Shield,
  Award,
  MapPin
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const SupportCoordinationPage = () => {
  const supportLevels = [
    {
      level: "1",
      title: "Support Connection",
      description: "Have you been left to figure things out on your own? That stops here. We build your confidence to connect with informal, community, and funded supports — and we walk beside you until you feel ready to stand on your own.",
      icon: Target
    },
    {
      level: "2",
      title: "Support Coordination (Coordination of Supports)",
      description: "Too many participants are told what they can't have instead of what they can. We work closely with you to break down your plan, find every opportunity within it, and build a life of independence, genuine relationships, and real community connection — on your terms.",
      icon: Users
    },
    {
      level: "3",
      title: "Specialist Support Coordination",
      description: "If your needs are complex, you deserve a coordinator who doesn't shy away from the hard conversations. Our specialist coordinators fight for stability in your support arrangements and ensure nothing falls through the cracks — because you've been let down enough.",
      icon: Award
    }
  ];

  const benefits = [
    "We actually explain your plan in plain language — no jargon, no confusion, no brushing you off",
    "We coordinate service bookings and agreements so you're never left chasing providers",
    "We connect you to services that genuinely care — not just the cheapest or easiest option",
    "We manage relationships with multiple providers so the burden doesn't fall on you",
    "We handle plan management, implementation reports, and the paperwork you shouldn't have to worry about",
    "When life throws you a curveball, we step in — not step back",
    "We build your independence and capacity because your growth matters to us",
    "We maintain real relationships with your support workers, providers, and family — because communication should never be a barrier"
  ];

  const services = [
    "Supported independent living (SIL) providers",
    "Specialist disability accommodation (SDA)",
    "Employment services",
    "Community access",
    "Daily living support",
    "Residential aged care",
    "Assistive technology",
    "Home modifications",
    "Mental health support",
    "Allied health"
  ];

  const faqs = [
    {
      question: "Who is eligible for NDIS support coordination?",
      answer: "If you need help understanding your NDIS plan, connecting with the right providers, or simply want someone in your corner who genuinely listens — you're eligible. At Life Care and Wellness, we don't just tick boxes. We simplify your journey and align every support to your actual goals, not someone else's idea of what you need."
    },
    {
      question: "How often will I meet with my NDIS Support Coordinator?",
      answer: "As often as you need. Some participants prefer weekly check-ins, others fortnightly or monthly. The difference with us? We don't disappear between meetings. You are always in control of the schedule, and we are here when you need us — not just when it's convenient for us."
    },
    {
      question: "How can NDIS Support Coordination help me use my plan effectively?",
      answer: "Most participants we meet have been underutilising their plans — not because they don't care, but because nobody took the time to show them what's possible. We break your plan open, show you every dollar and every opportunity, and connect you with supports that actually move the needle."
    },
    {
      question: "What should I expect during my first meeting with a Support Coordinator?",
      answer: "Expect to be heard. Our first meeting isn't about paperwork — it's about you. We want to understand your life, your frustrations, your hopes. We set clear expectations and build a path forward together. No rushed conversations, no feeling like a number."
    },
    {
      question: "Do I need to pay for NDIS Support Coordination out of pocket?",
      answer: "No. If Support Coordination is included in your NDIS plan, the funding comes from your Capacity Building budget. You won't pay a cent out of pocket. And if it's not in your plan yet, we can help you understand how to request it at your next review."
    },
    {
      question: "How soon can I start receiving support coordination after approval?",
      answer: "Usually within 24 hours of your plan being accessible. We don't make you wait weeks while your life sits on hold. As soon as your plan is live, we're ready to move."
    },
    {
      question: "Can I change my NDIS Support Coordinator if I'm not happy?",
      answer: "Absolutely — and you should never feel guilty about it. You deserve a coordinator who respects you, listens to you, and fights for you. If your current provider isn't doing that, reach out to us. We'll make the transition as smooth as possible."
    },
    {
      question: "What's included in NDIS Support Coordination services?",
      answer: "Everything from understanding your plan, connecting with providers, managing service agreements, crisis support, plan reviews, and ongoing advocacy. But more than that — you get a team that genuinely cares about your outcomes, not just their billable hours."
    },
    {
      question: "How do I request support coordination in my NDIS plan?",
      answer: "You can request it through a service request or raise it during your plan review. If you're unsure how, contact us — we'll walk you through every step and make sure your voice is heard by the NDIA."
    },
    {
      question: "Can NDIS Support Coordination help me find service providers?",
      answer: "That's one of the most important things we do. But we don't just hand you a list. We vet providers, match them to your specific needs, and hold them accountable. You deserve providers who show up — and we make sure they do."
    },
    {
      question: "What's the difference between a Support Coordinator and a Local Area Coordinator (LAC)?",
      answer: "An LAC works for the NDIA and helps with planning within a specific area. A Support Coordinator works for you. We're funded through your plan specifically to coordinate your services, advocate on your behalf, and ensure you get the outcomes you deserve — not just the ones that are easiest to deliver."
    },
    {
      question: "Can NDIS Support Coordination assist during plan reviews or changes?",
      answer: "This is where having the right coordinator makes all the difference. We help you prepare evidence, articulate your needs, and ensure nothing is overlooked. Too many participants lose funding at review time because they weren't properly supported. That won't happen on our watch."
    }
  ];

  const scrollToContact = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="NDIS Support Coordination" description="Professional NDIS Support Coordination in Brisbane and Logan. We break down your plan, fight for your rights, and build an action plan tailored to your unique needs." />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                NDIS Support Coordination
              </h1>
              <p 
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                Do you have a thorough understanding of your NDIS plan and what you are entitled to? Our Support Coordinators in Logan, Brisbane will break down your plan and build an action plan tailored to meet your needs. Your unique needs, wants, and talents will be found and explored and your goals executed.
              </p>
              <p 
                className="text-lg md:text-xl leading-relaxed mb-6"
                style={{ color: '#5e5e5e' }}
              >
                Have your interests, aspirations and requests been rejected? Life Care And Wellness will exhaust and explore every avenue to make this happen for you. Peace of mind is paramount and we will take every step of the journey along with you.
              </p>
              <p 
                className="text-base leading-relaxed italic mb-6"
                style={{ color: '#6c935e' }}
              >
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={images.supportCoord1}
                alt="NDIS Support Coordination services"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Do You Feel Trapped Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Do You Feel Trapped by Your Current Supports?
              </h2>
              <p 
                className="text-lg md:text-xl mb-4"
                style={{ color: '#5e5e5e' }}
              >
                Too many NDIS participants are left feeling unheard, dismissed, and stuck with providers who treat them like a number on a spreadsheet. The reality is that many NDIS providers do not treat people with the respect, mindfulness, and genuine care that every person deserves.
              </p>
              <p 
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                We can help set you free from that cycle. At Life Care and Wellness, we put you back in the driver's seat of your own life.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={images.supportCoord2}
                  alt="Breaking free with the right NDIS support"
                  className="w-full h-[350px] object-cover"
                />
              </div>
              <div>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  Have you been told "that's not in your plan" without anyone actually checking? Have your calls gone unanswered for days? Have you felt like your coordinator was doing the bare minimum? You're not alone — and it doesn't have to be this way.
                </p>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  Our coordinators don't just manage your plan — they champion your cause. We dig into every line of your funding, challenge the things that aren't working, and connect you with providers who actually show up and deliver.
                </p>
                <p 
                  className="text-lg leading-relaxed"
                  style={{ color: '#5e5e5e' }}
                >
                  You deserve to feel safe, respected, and genuinely supported. That's not a luxury — it's your right.
                </p>
              </div>
            </div>

            <div 
              className="p-8 rounded-2xl mb-12"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <h3 
                className="text-2xl font-bold mb-6 text-center"
                style={{ color: '#587740' }}
              >
                We Connect You With Providers Who Actually Care
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 
                      className="w-5 h-5 mt-1 flex-shrink-0"
                      style={{ color: '#81b956' }}
                    />
                    <p className="text-base" style={{ color: '#5e5e5e' }}>
                      {service}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center p-6 rounded-xl bg-white shadow-md">
              <p 
                className="text-lg leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                If Support Coordination isn't in your plan yet, don't worry. Our NDIS Exploration & Mentoring packages can guide you through the process of getting it included — because you shouldn't have to navigate this system alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three Levels Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Three Levels of Support — Which One Do You Need?
              </h2>
              <p 
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                No matter where you are on your NDIS journey, there's a level of coordination designed for you. The question isn't whether you need support — it's whether you're getting the right kind.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {supportLevels.map((level, index) => {
                const Icon = level.icon;
                return (
                  <Card 
                    key={index}
                    className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <CardContent className="p-8">
                      <div 
                        className="w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto text-3xl font-bold text-white"
                        style={{ backgroundColor: '#81b956' }}
                      >
                        {level.level}
                      </div>
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto"
                        style={{ backgroundColor: '#AABC5A' }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 
                        className="text-xl font-bold mb-4 text-center"
                        style={{ color: '#587740' }}
                      >
                        {level.title}
                      </h3>
                      <p 
                        className="text-base leading-relaxed text-center"
                        style={{ color: '#5e5e5e' }}
                      >
                        {level.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Why We're Different From Other NDIS Providers
              </h2>
              <p 
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                Let's be honest — the NDIS system is full of providers who overpromise and underdeliver. We've seen participants bounce from coordinator to coordinator, feeling more frustrated each time. That ends with us.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p 
                  className="text-lg leading-relaxed mb-6"
                  style={{ color: '#5e5e5e' }}
                >
                  We are NDIS providers across Australia. Our passionate Support Coordinators have lived experience with people with a disability and bring real empathy — not just qualifications on paper. We provide clear, honest, and unbiased advice because you deserve the truth, not a watered-down version of it.
                </p>
                <p 
                  className="text-lg leading-relaxed mb-6"
                  style={{ color: '#5e5e5e' }}
                >
                  Unlike providers who treat your plan like a transaction, we treat it like your life — because that's exactly what it is. We respect your thoughts, your wishes, and your decisions. We don't talk over you, we don't rush you, and we never make decisions without you. Once your Support Coordinator is working with you, they will respond to your contact within 24 hours — not days, not "when they get around to it."
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.personalTraining}
                  alt="Compassionate support coordination that puts you first"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div 
              className="p-8 rounded-2xl text-center"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <p 
                className="text-xl font-semibold mb-4"
                style={{ color: '#587740' }}
              >
                Ready to experience what real support coordination feels like? Reach out to us — you can fill out our contact form or email us directly at admin@lifecarewellness.au
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity mt-4"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                How We Set You Free
              </h2>
              <p 
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                Feeling overwhelmed by the NDIS? Feeling like nobody is truly listening? Here's how we change that — from your very first conversation with us.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: '#81b956' }}
                >
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <h3 
                  className="text-xl font-bold mb-4"
                  style={{ color: '#587740' }}
                >
                  We Listen — Really Listen
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: '#5e5e5e' }}
                >
                  Our first step isn't pulling out forms. It's sitting down with you and hearing your story — your frustrations, your dreams, what's been working and what hasn't. We learn who you are as a person, not just a participant number. Your unique needs, wants, and talents become the foundation of everything we do.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: '#7A993B' }}
                >
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 
                  className="text-xl font-bold mb-4"
                  style={{ color: '#587740' }}
                >
                  We Take Action — Relentlessly
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: '#5e5e5e' }}
                >
                  Once we understand your goals, we don't sit on them. We break down your plan, identify every opportunity, connect you with providers who share our values, and adjust supports as your life changes. If something isn't working, we fix it — fast. If a provider isn't delivering, we hold them accountable or find someone who will.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                What a Support Coordinator Should Actually Do For You
              </h2>
              <p 
                className="text-lg md:text-xl mb-8"
                style={{ color: '#5e5e5e' }}
              >
                If your current coordinator isn't doing these things, it might be time to ask yourself — are they really working for you, or just working through you?
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-start space-x-4 p-6 rounded-xl hover:shadow-md transition-shadow"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <CheckCircle2 
                    className="w-6 h-6 flex-shrink-0 mt-1"
                    style={{ color: '#81b956' }}
                  />
                  <p 
                    className="text-base leading-relaxed"
                    style={{ color: '#5e5e5e' }}
                  >
                    {benefit}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Get the Support You Deserve
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border rounded-lg px-6 shadow-sm bg-white"
                >
                  <AccordionTrigger 
                    className="text-left hover:no-underline py-6"
                    style={{ color: '#587740' }}
                  >
                    <span className="text-lg font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent 
                    className="text-base leading-relaxed pb-6"
                    style={{ color: '#5e5e5e' }}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              You Deserve Better. Let's Make It Happen.
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Stop settling for support that doesn't support you. Life Care and Wellness will exhaust every avenue, explore every option, and walk every step of the journey with you. Your peace of mind is not negotiable — it's our promise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-base px-8 py-6"
                style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us Today
              </Button>
              <a href="/get-on-ndis-free">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10"
                >
                  Get on the NDIS Free
                </Button>
              </a>
            </div>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default SupportCoordinationPage;
